import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { catchError, filter, firstValueFrom, map, of, startWith, type Observable } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  NbAvatar,
  NbButton,
  NbCommandPalette,
  NbDrawer,
  NbIcon,
  NbIconButton,
  NbSideBrand,
  NbSideFoot,
  NbSideNavItem,
  NbSideNavSection,
  NbSidebar,
  NbToastHost,
  NbTopbar,
  ThemeService,
  initials,
  type CommandItem,
} from '@nabotavle/ui';
import { AuthService } from '@nabotavle/auth';
import { LocaleService } from '@nabotavle/util';
import {
  DokumenterService,
  SakerService,
  StyreService,
  type ArchiveTree,
  type BoardCaseSummary,
  type OwnProfile,
  type RegisterPage,
  type StyreDashboard as StyreDashboardView,
  type TenantConfigView,
} from '@nabotavle/data-access/core';
import { ArrowRight, Bell, ExternalLink, FileText, Inbox, LogOut, Megaphone, Moon, Plus, ShieldAlert, Sun, UserPlus, Users } from 'lucide';
import { VarslerDrawer } from './shell/varsler-drawer';

interface NavLink {
  /** i18n key (`nav.*`). */
  readonly label: string;
  readonly path: string;
  /** When true the page is duplicated inside the console — navigate internally (not a cross-app deep link). */
  readonly internal?: boolean;
  /** Which dashboard counter feeds this item's badge (S6). */
  readonly badge?: 'openSaker' | 'oppgaverMedFrist';
}
interface NavGroup {
  /** i18n key (`nav.gruppe.*`). */
  readonly label: string;
  readonly items: readonly NavLink[];
}

/** S1: the prototype's four groups + «Beboerappen» (the in-console copies, V1.12) as a fifth. Labels are i18n keys. */
const NAV_GROUPS: readonly NavGroup[] = [
  { label: 'nav.gruppe.oversikt', items: [
    { label: 'nav.dashboard', path: '/dashboard' },
    { label: 'nav.oppgaver', path: '/oppgaver', badge: 'oppgaverMedFrist' },
  ] },
  { label: 'nav.gruppe.beboere', items: [
    { label: 'nav.saker', path: '/saker', badge: 'openSaker' },
    { label: 'nav.kommunikasjon', path: '/kommunikasjon' },
    { label: 'nav.register', path: '/register' },
    { label: 'nav.undersokelser', path: '/undersokelser' },
  ] },
  { label: 'nav.gruppe.drift', items: [
    { label: 'nav.dugnad', path: '/dugnad' },
    { label: 'nav.vedlikehold', path: '/vedlikehold' },
    { label: 'nav.dokumenter', path: '/dokumenter' },
    { label: 'nav.nodvarsel', path: '/nodvarsel' },
  ] },
  { label: 'nav.gruppe.administrasjon', items: [
    { label: 'nav.forvalterLenker', path: '/forvalter-lenker' },
    { label: 'nav.portefolje', path: '/forvalter' },
    { label: 'nav.innstillinger', path: '/innstillinger' },
  ] },
  { label: 'nav.gruppe.beboerappen', items: [
    { label: 'nav.home', path: '/hjem', internal: true },
    { label: 'nav.booking', path: '/booking', internal: true },
    { label: 'nav.oppslag', path: '/oppslag', internal: true },
    { label: 'nav.naboer', path: '/naboer', internal: true },
  ] },
];

/** One API hit for the ⌘K palette, kept raw so kind/sub re-translate on a locale switch. */
interface SearchHit {
  readonly id: string;
  readonly kind: 'sak' | 'beboer' | 'dokument';
  readonly label: string;
  readonly subParams: Record<string, string>;
  /** Where the hit navigates (`/saker?open=…`, `/register?q=…`, `/dokumenter?q=…`). */
  readonly target: string;
  readonly keywords: string;
}

/** Debounce for the palette's API search (S3 step 2). */
const SEARCH_DEBOUNCE_MS = 250;
/** Minimum trimmed length before the palette searches the API. */
const SEARCH_MIN_CHARS = 2;
/** Max hits per source. */
const SEARCH_PER_SOURCE = 5;

/**
 * Styre console shell (M8 → 2026-09 redesign, phase C). Its own desktop Angular app.
 *
 * Decisions: S1 four prototype groups + «Beboerappen» kept as a fifth group; S3 the
 * topbar search is a ⌘K trigger for `NbCommandPalette` — pages + actions, plus saker /
 * beboere / dokumenter fetched through the EXISTING tenant-filtered Styre endpoints
 * (`/api/styre/saker`, `/api/styre/register`, `/api/dokumenter`), debounced and
 * client-filtered, never a cross-cutting search endpoint; S6 badge counts render as a separate element; the bell
 * opens a varselskuff (`NbDrawer`). Chrome is hidden on /login. All chrome strings are
 * i18n keys (phase E): the template uses `| translate`; strings handed to child components
 * or built in TS go through `TranslateService.instant` inside a `computed` that reads
 * `currentLang()` so they re-render on a locale switch.
 */
@Component({
  selector: 'styre-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    NbSidebar,
    NbSideBrand,
    NbSideNavSection,
    NbSideNavItem,
    NbSideFoot,
    NbTopbar,
    NbAvatar,
    NbButton,
    NbIconButton,
    NbIcon,
    NbCommandPalette,
    NbToastHost,
    TranslatePipe,
  ],
  // NOTE: exactly ONE <router-outlet>. The chrome (sidebar + topbar) is toggled
  // around it, never duplicated into a second outlet — two outlets in @if/@else
  // branches leave the router unable to re-render after showChrome() flips
  // (login → dashboard), which blanks the page.
  template: `<div class="shell" [class.bare]="!showChrome()">
      @if (showChrome()) {
        <nb-sidebar [ariaLabel]="'shell.menu' | translate">
          <nb-side-brand [name]="lagName()" [sub]="'shell.sub' | translate" [mark]="lagInitials()" />

          @for (group of navGroups; track group.label) {
            <nb-side-nav-section>{{ group.label | translate }}</nb-side-nav-section>
            @for (item of group.items; track item.path) {
              <nb-side-nav-item
                [active]="isActive(item.path)"
                [count]="badgeFor(item)"
                [countLabel]="'shell.count' | translate"
                (activated)="go(item.path)"
              >{{ item.label | translate }}</nb-side-nav-item>
            }
          }

          <nb-side-foot>
            <div class="foot-id">
              <nb-avatar [color]="1" size="m" [initials]="userInitials()" />
              <div class="grow"><div class="foot-name">{{ userName() }}</div><div class="foot-role small">{{ role() }}</div></div>
              <button class="logout" type="button" [attr.aria-label]="'common.logout' | translate" (click)="logout()"><nb-icon [icon]="LogOut" /></button>
            </div>
          </nb-side-foot>
        </nb-sidebar>
      }

      <div class="main">
        @if (showChrome()) {
          <nb-topbar [command]="true" [searchPlaceholder]="'shell.searchPlaceholder' | translate" (commandOpen)="cmdOpen.set(true)">
            <nb-icon-button [web]="true" [label]="'shell.toResident' | translate" (click)="openResident('/hjem')"><nb-icon [icon]="ExternalLink" /></nb-icon-button>
            <nb-icon-button [web]="true" [label]="'shell.notifications' | translate" [count]="unread()" (click)="openVarsler()"><nb-icon [icon]="Bell" /></nb-icon-button>
            <nb-button variant="pri" size="sm" (click)="go('/kommunikasjon')"><nb-icon [icon]="Plus" /> {{ 'shell.newPost' | translate }}</nb-button>
            <nb-avatar [color]="1" size="s" [initials]="userInitials()" />
          </nb-topbar>
          <nb-command-palette
            [items]="cmdItems()"
            [(open)]="cmdOpen"
            [placeholder]="'palett.placeholder' | translate"
            [ariaLabel]="'palett.ariaLabel' | translate"
            [emptyText]="cmdEmptyText()"
            [loading]="searching()"
            [loadingText]="'palett.search.searching' | translate"
            (queryChange)="onCmdQuery($event)"
            (picked)="runCmd($event)"
          />
          <nb-toast-host bottom="24px" />
        }

        <main class="content" [class.bare]="!showChrome()"><router-outlet /></main>
      </div>
    </div>`,
  styles: `
    :host { display: block; min-block-size: 100dvh; }
    .shell { display: flex; min-block-size: 100dvh; }
    .shell.bare { display: block; }
    .main { flex: 1; min-inline-size: 0; display: flex; flex-direction: column; background: var(--bg); }
    .content { flex: 1; padding: 24px 28px; max-inline-size: 1180px; inline-size: 100%; }
    .content.bare { padding: 0; max-inline-size: none; }
    .foot-id { display: flex; align-items: center; gap: 10px; }
    .foot-id .grow { flex: 1; min-inline-size: 0; }
    .foot-name { font-weight: 700; font-size: 13px; }
    .foot-role { color: var(--faint); }
    .logout { position: relative; background: none; border: none; color: var(--faint); cursor: pointer; --nb-icon-size: 18px; display: grid; place-items: center; padding: 6px; border-radius: var(--r-sm); }
    /* 30px visual, 44px hit area (WCAG 2.5.8). */
    .logout::before { content: ''; position: absolute; inset: -7px; }
    .logout:hover { color: var(--text); }
    .logout:focus-visible { outline: none; box-shadow: var(--ring); }
    nb-topbar nb-button { --nb-icon-size: 17px; }
  `,
})
export class App {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly drawer = inject(NbDrawer);
  private readonly theme = inject(ThemeService);
  private readonly t = inject(TranslateService);
  private readonly saker = inject(SakerService);
  private readonly styre = inject(StyreService);
  private readonly dokumenter = inject(DokumenterService);
  private readonly localeService = inject(LocaleService);
  protected readonly Bell = Bell;
  protected readonly ExternalLink = ExternalLink;
  protected readonly LogOut = LogOut;
  protected readonly Plus = Plus;

  protected readonly navGroups = NAV_GROUPS;

  // Gated on auth: the root component also lives on /login, where an eager fetch would 401.
  private readonly config = httpResource<TenantConfigView>(() =>
    this.auth.isAuthenticated() ? '/api/min-konfigurasjon' : undefined,
  );
  private readonly profile = httpResource<OwnProfile>(() =>
    this.auth.isAuthenticated() ? '/api/profil/me' : undefined,
  );
  /** Real counts for the sidebar badges (S6) — same source as the dashboard KPI cards. */
  private readonly dash = httpResource<StyreDashboardView>(() =>
    this.auth.isAuthenticated() ? '/api/styre/dashboard' : undefined,
  );
  private readonly unreadRes = httpResource<{ count: number }>(() =>
    this.auth.isAuthenticated() ? '/api/varsler/uleste-antall' : undefined,
  );
  protected readonly lagName = computed(() => this.config.value()?.branding?.displayName || this.tr('shell.lagFallback'));
  protected readonly lagInitials = computed(() => initials(this.lagName()));
  protected readonly userName = computed(() => this.profile.value()?.displayName || this.tr('shell.userFallback'));
  protected readonly userInitials = computed(() => initials(this.userName()));
  protected readonly role = computed(() => this.auth.activeRole() || this.tr('shell.userFallback'));
  protected readonly unread = computed(() => this.unreadRes.value()?.count ?? 0);

  protected readonly cmdOpen = signal(false);
  /** Trimmed palette query — the stale-result guard compares against it after each fetch. */
  private readonly cmdQuery = signal('');
  protected readonly searching = signal(false);
  private readonly searchHits = signal<readonly SearchHit[]>([]);
  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );
  // Pre-login routes (login, glemt passord, nytt passord) render without sidebar/topbar.
  protected readonly showChrome = computed(() => !/^\/(login|glemt-passord|nytt-passord)(?=[/?#]|$)/.test(this.url()));

  /** ⌘K palette: every sidebar page + the prototype's actions + the debounced API hits (S3 step 2). */
  protected readonly cmdItems = computed<CommandItem[]>(() => {
    const side = this.tr('palett.kindSide');
    const act = this.tr('palett.kindHandling');
    const icons = { sak: Inbox, beboer: Users, dokument: FileText } as const;
    return [
      ...this.navGroups.flatMap((g) => g.items.map((i) => ({
        id: `page:${i.path}`, label: this.tr(i.label), sub: this.tr('palett.goTo', { group: this.tr(g.label).toLowerCase() }), kind: side, icon: ArrowRight,
      }))),
      { id: 'act:nytt-oppslag', label: this.tr('palett.nyttOppslag'), sub: this.tr('palett.nyttOppslagSub'), kind: act, icon: Megaphone },
      { id: 'act:inviter', label: this.tr('palett.inviter'), sub: this.tr('palett.inviterSub'), kind: act, icon: UserPlus },
      { id: 'act:nodvarsel', label: this.tr('palett.nodvarsel'), sub: this.tr('palett.nodvarselSub'), kind: act, icon: ShieldAlert },
      { id: 'act:tema', label: this.tr(this.theme.isDark() ? 'palett.temaLyst' : 'palett.temaMorkt'), sub: this.tr('palett.temaSub'), kind: act, icon: this.theme.isDark() ? Sun : Moon },
      { id: 'act:beboerapp', label: this.tr('palett.beboerapp'), sub: this.tr('palett.beboerappSub'), kind: act, icon: ExternalLink },
      ...this.searchHits().map((h) => ({
        id: h.id,
        label: h.label,
        sub: this.tr(`palett.search.sub.${h.kind}`, h.subParams),
        kind: this.tr(`palett.search.kind.${h.kind}`),
        icon: icons[h.kind],
        keywords: h.keywords,
      })),
    ];
  });
  /** Below two characters the API is not asked, so tell the user why instead of «Ingen treff». */
  protected readonly cmdEmptyText = computed(() =>
    this.tr(this.cmdQuery().length < SEARCH_MIN_CHARS ? 'palett.search.searchHint' : 'palett.empty'),
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.searchTimer));

    // Server-side locale precedence (profile > local choice > lag default), same as the resident shell:
    // applied once both authenticated resources have settled, and guarded on the value pair so it runs
    // once per change and never fights the console's own Språk switch.
    let appliedKey: string | undefined;
    effect(() => {
      if (!this.auth.isAuthenticated()) return;
      const profileSettled = this.profile.hasValue() || !!this.profile.error();
      const configSettled = this.config.hasValue() || !!this.config.error();
      if (!profileSettled || !configSettled) return;
      const profileLocale = this.profile.hasValue() ? this.profile.value()?.locale : undefined;
      const tenantDefault = this.config.hasValue() ? this.config.value()?.defaultLocale : undefined;
      const key = `${profileLocale ?? ''}|${tenantDefault ?? ''}`;
      if (key === appliedKey) return;
      appliedKey = key;
      this.localeService.applyServerPreference(profileLocale, tenantDefault);
    });
  }

  /** Palette keystrokes: debounce, then fetch from the three Styre endpoints in parallel. */
  protected onCmdQuery(raw: string): void {
    const q = raw.trim();
    this.cmdQuery.set(q);
    clearTimeout(this.searchTimer);
    if (q.length < SEARCH_MIN_CHARS) {
      this.searchHits.set([]);
      this.searching.set(false);
      return;
    }
    this.searchTimer = setTimeout(() => void this.search(q), SEARCH_DEBOUNCE_MS);
  }

  private async search(q: string): Promise<void> {
    this.searching.set(true);
    const lc = q.toLowerCase();
    // Each source fails independently — a 5xx from one must not hide the others or the page/action items.
    const safe = <T>(o: Observable<T>, fallback: T): Promise<T> => firstValueFrom(o.pipe(catchError(() => of(fallback))));
    const [cases, reg, tree] = await Promise.all([
      safe<BoardCaseSummary[]>(this.saker.boardList(), []),
      safe<RegisterPage>(this.styre.register({ q, pageSize: SEARCH_PER_SOURCE }), { rows: [], total: 0 }),
      safe<ArchiveTree>(this.dokumenter.tree(), { folders: [], documents: [] }),
    ]);
    if (this.cmdQuery() !== q) return; // stale — a newer query (or a close) superseded this one
    const folderName = new Map(tree.folders.map((f) => [f.id, f.name]));
    const statusLabel = (status: string): string => {
      const key = `saker.status.${status}`;
      const label = this.tr(key);
      return label === key ? status : label;
    };
    const hits: SearchHit[] = [
      ...cases
        .filter((c) => c.title.toLowerCase().includes(lc) || `#${c.caseNumber}`.includes(lc))
        .slice(0, SEARCH_PER_SOURCE)
        .map((c): SearchHit => ({
          id: `sak:${c.id}`, kind: 'sak', label: c.title, keywords: `#${c.caseNumber} ${q}`,
          subParams: { number: String(c.caseNumber), status: statusLabel(c.status) },
          target: `/saker?open=${encodeURIComponent(c.id)}`,
        })),
      // The register endpoint filters server-side, so `q` goes into keywords to keep the palette's own filter from dropping the hit.
      ...reg.rows.slice(0, SEARCH_PER_SOURCE).map((r): SearchHit => ({
        id: `beboer:${r.unitId}`, kind: 'beboer', label: r.ownerName || r.unitLabel, keywords: `${r.unitLabel} ${q}`,
        subParams: { unit: r.unitLabel, role: r.role },
        target: `/register?q=${encodeURIComponent(r.unitLabel)}`,
      })),
      ...tree.documents
        .filter((d) => d.title.toLowerCase().includes(lc) || d.tags.some((t) => t.toLowerCase().includes(lc)))
        .slice(0, SEARCH_PER_SOURCE)
        .map((d): SearchHit => ({
          id: `dok:${d.id}`, kind: 'dokument', label: d.title, keywords: `${d.tags.join(' ')} ${q}`,
          subParams: { folder: (d.folderId && folderName.get(d.folderId)) || '' },
          target: `/dokumenter?q=${encodeURIComponent(d.title)}`,
        })),
    ];
    this.searchHits.set(hits);
    this.searching.set(false);
  }

  /** `instant()` that also tracks the active locale, so enclosing `computed`s re-evaluate on a switch. */
  private tr(key: string, params?: Record<string, unknown>): string {
    this.t.currentLang();
    return this.t.instant(key, params);
  }

  /** Sidebar badge: only when the counter is positive, else hidden (null). */
  protected badgeFor(item: NavLink): number | null {
    if (!item.badge) return null;
    const n = this.dash.value()?.[item.badge] ?? 0;
    return n > 0 ? n : null;
  }

  protected runCmd(item: CommandItem): void {
    if (item.id.startsWith('page:')) return this.go(item.id.slice(5));
    const hit = this.searchHits().find((h) => h.id === item.id);
    if (hit) return this.go(hit.target);
    if (item.id.startsWith('sak:')) return this.go(`/saker?open=${encodeURIComponent(item.id.slice(4))}`);
    switch (item.id) {
      case 'act:nytt-oppslag': return this.go('/kommunikasjon');
      case 'act:inviter': return this.go('/register');
      case 'act:nodvarsel': return this.go('/nodvarsel');
      case 'act:tema': return this.theme.toggleDark();
      case 'act:beboerapp': return this.openResident('/hjem');
    }
  }

  protected openVarsler(): void {
    const ref = this.drawer.open(VarslerDrawer, { size: 'md', ariaLabel: this.t.instant('skuff.varsler.title') });
    ref.closed.subscribe(() => this.unreadRes.reload());
  }

  protected isActive(path: string): boolean {
    const u = this.url();
    return u === path || u.startsWith(path + '/') || u.startsWith(path + '?');
  }
  protected go(path: string): void {
    void this.router.navigateByUrl(path);
  }

  /** Deep-link across to the resident app (kundeapp) — separate origin. */
  protected openResident(path: string): void {
    const h = location.hostname;
    const base = h === 'localhost' || h === '127.0.0.1' ? 'http://localhost:4200' : `${location.protocol}//${h.replace(/^styre\./, 'app.')}`;
    location.assign(base + path);
  }

  protected logout(): void {
    this.auth.logout();
    void this.router.navigateByUrl('/login');
  }
}
